////////////////////////////////////////////////////////////////////////////////
//                                                                            //
//  IRONSIDE - Public Website GUI                                             //
//                                                                            //
//  Version: 1.0.6                                                            //
//  Summary: Contact enquiry form — POST /public/enquiries (1.0.6.md §1)     //
//                                                                            //
////////////////////////////////////////////////////////////////////////////////

import { useEffect, useRef, useState, type FormEvent } from 'react';
import { Link } from 'react-router';

import api from '@/lib/api';
import { useApi } from '@/hooks/useApi';
import { loadTurnstile, resetTurnstile } from '@/lib/turnstile';
import { Field } from '@/components/forms/Field';
import { TextInput } from '@/components/forms/TextInput';
import { TextArea } from '@/components/forms/TextArea';
import { Select, type SelectOption } from '@/components/forms/Select';
import { Button } from '@/components/forms/Button';

// Client-exposable feature-config subset served by GET /public/config (1.0.5/1.0.6
// foundations contract) — everything else in the API's cfg.features.* stays
// server-side. `bot` is present in the payload but this component only reads
// `enquiries`; typed loosely so the bot widget's shape can evolve independently.
export interface PublicFeaturesConfig {
  enquiries?: {
    enabled?: boolean;
    turnstile?: { site_key?: string | null };
  };
}

// Values are the content.enquiries enquiry_type slugs (migration 0006). The
// Careers option never submits — it links out to /careers instead (§1.1); Media
// routes the same as General server-side.
const ENQUIRY_TYPES: SelectOption[] = [
  { value: 'general', label: 'General' },
  { value: 'project', label: 'Project' },
  { value: 'supplier', label: 'Supplier & Subcontractor' },
  { value: 'careers', label: 'Careers' },
  { value: 'media', label: 'Media' },
];

const NAME_MAX = 120;
const COMPANY_MAX = 120;
const PHONE_MAX = 20;
const MESSAGE_MIN = 20;
const MESSAGE_MAX = 2000;

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
// AU-lenient: digits with the usual separators, optional leading +.
const PHONE_RE = /^\+?[\d\s().-]{5,}$/;

interface Fields {
  name: string;
  email: string;
  phone: string;
  company: string;
  message: string;
}

type FieldErrors = Partial<Record<keyof Fields | 'enquiryType', string>>;

function validate (fields: Fields, enquiryType: SelectOption | null): FieldErrors {
  const errors: FieldErrors = {};

  const name = fields.name.trim();
  if (!name) errors.name = 'Please enter your name.';
  else if (name.length > NAME_MAX) errors.name = `Name must be ${NAME_MAX} characters or fewer.`;

  const email = fields.email.trim();
  if (!email) errors.email = 'Please enter your email address.';
  else if (!EMAIL_RE.test(email)) errors.email = 'Please enter a valid email address.';

  const phone = fields.phone.trim();
  if (phone && (phone.length > PHONE_MAX || !PHONE_RE.test(phone))) {
    errors.phone = 'Please enter a valid phone number.';
  }

  if (fields.company.trim().length > COMPANY_MAX) {
    errors.company = `Company must be ${COMPANY_MAX} characters or fewer.`;
  }

  if (!enquiryType) errors.enquiryType = 'Please select an enquiry type.';

  const message = fields.message.trim();
  if (message.length < MESSAGE_MIN) errors.message = `Message must be at least ${MESSAGE_MIN} characters.`;
  else if (message.length > MESSAGE_MAX) errors.message = `Message must be ${MESSAGE_MAX} characters or fewer.`;

  return errors;
}

// exactOptionalPropertyTypes: Field's `error?: string` cannot receive an explicit
// undefined — spread the prop only when an error actually exists.
function errProp (message: string | undefined): { error: string } | Record<string, never> {
  return message === undefined ? {} : { error: message };
}

export function EnquiryForm () {
  const config = useApi<PublicFeaturesConfig>('/public/config');

  const [fields, setFields] = useState<Fields>({ name: '', email: '', phone: '', company: '', message: '' });
  const [enquiryType, setEnquiryType] = useState<SelectOption | null>(null);
  const [errors, setErrors] = useState<FieldErrors>({});
  const [honeypot, setHoneypot] = useState('');
  const [phase, setPhase] = useState<'editing' | 'submitting' | 'success'>('editing');
  const [submitFailed, setSubmitFailed] = useState(false);
  const [interacted, setInteracted] = useState(false);
  const [turnstileToken, setTurnstileToken] = useState<string | null>(null);

  // Minimum-fill-time reference — epoch ms captured once at mount; the API's
  // `form_ts` contract (public.enquiries.ts) is a number, not an ISO string.
  // Set in an effect, not at render (react-hooks/purity: Date.now() is impure).
  const startedAtRef = useRef(0);
  const turnstileHostRef = useRef<HTMLDivElement | null>(null);
  const widgetIdRef = useRef<string | null>(null);

  useEffect(() => {
    if (startedAtRef.current === 0) startedAtRef.current = Date.now();
  }, []);

  const siteKey = config.status === 'success'
    ? config.data.enquiries?.turnstile?.site_key ?? null
    : null;
  // Config unavailable (loading/error) renders the form optimistically — the API
  // remains the enforcement point for both the flag and Turnstile.
  const enquiriesDisabled = config.status === 'success' && config.data.enquiries?.enabled === false;

  // Explicit render on first interaction, never on page load (§1.3).
  useEffect(() => {
    if (!interacted || !siteKey || widgetIdRef.current !== null) return;
    let cancelled = false;
    loadTurnstile()
      .then((turnstile) => {
        if (cancelled || widgetIdRef.current !== null || !turnstileHostRef.current) return;
        widgetIdRef.current = turnstile.render(turnstileHostRef.current, {
          sitekey: siteKey,
          theme: 'auto',
          callback: (token) => setTurnstileToken(token),
          'expired-callback': () => setTurnstileToken(null),
          'error-callback': () => setTurnstileToken(null),
        });
      })
      .catch(() => { /* widget unavailable — submit proceeds tokenless, API decides */ });
    return () => { cancelled = true; };
  }, [interacted, siteKey]);

  const setField = (key: keyof Fields) => (value: string) => {
    setFields((prev) => ({ ...prev, [key]: value }));
  };

  const onSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (phase === 'submitting') return;

    const nextErrors = validate(fields, enquiryType);
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length > 0 || !enquiryType) return;

    setSubmitFailed(false);
    setPhase('submitting');
    try {
      // Field names/types are the API's zod contract (public.enquiries.ts):
      // `website` is the honeypot, `form_ts` is epoch ms; empty optionals are
      // omitted rather than sent as null.
      const phone = fields.phone.trim();
      const company = fields.company.trim();
      await api.post('/public/enquiries', {
        name: fields.name.trim(),
        email: fields.email.trim(),
        ...(phone ? { phone } : {}),
        ...(company ? { company } : {}),
        enquiry_type: enquiryType.value,
        message: fields.message.trim(),
        website: honeypot,
        form_ts: startedAtRef.current,
        ...(turnstileToken ? { turnstile_token: turnstileToken } : {}),
      });
      setPhase('success');
    } catch {
      // Token is single-use — whatever the failure was, it is spent now.
      setTurnstileToken(null);
      resetTurnstile(widgetIdRef.current);
      setSubmitFailed(true);
      setPhase('editing');
    }
  };

  if (enquiriesDisabled) {
    return (
      <div role="note" className="mt-6 rounded border border-line bg-paper/60 p-4 text-ink">
        <p>
          The enquiry form is currently unavailable. In the meantime, please email your enquiry
          directly to{' '}
          <a href="mailto:info@ironside.com.au" className="underline">info@ironside.com.au</a>{' '}
          and a team member will respond shortly.
        </p>
      </div>
    );
  }

  if (phase === 'success') {
    return (
      <div role="status" className="mt-6 rounded border border-line bg-paper/60 p-4 text-ink">
        <p>Thanks — a team member will respond shortly.</p>
      </div>
    );
  }

  const isCareers = enquiryType?.value === 'careers';

  return (
    <form className="mt-6" noValidate onSubmit={(event) => { void onSubmit(event); }} onFocusCapture={() => setInteracted(true)}>
      {submitFailed && (
        <div role="alert" className="mb-6 rounded border border-red-600 bg-paper/60 p-4 text-ink">
          <p>
            Sorry — your enquiry couldn't be sent right now. Please try again, or email us
            directly at{' '}
            <a href="mailto:info@ironside.com.au" className="underline">info@ironside.com.au</a>.
          </p>
        </div>
      )}

      <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
        <Field label="Full name" {...errProp(errors.name)}>
          {({ inputId, describedBy }) => (
            <TextInput
              id={inputId}
              name="name"
              autoComplete="name"
              maxLength={NAME_MAX}
              required
              invalid={Boolean(errors.name)}
              aria-describedby={describedBy}
              value={fields.name}
              onChange={(e) => setField('name')(e.target.value)}
            />
          )}
        </Field>

        <Field label="Email" {...errProp(errors.email)}>
          {({ inputId, describedBy }) => (
            <TextInput
              id={inputId}
              type="email"
              name="email"
              autoComplete="email"
              required
              invalid={Boolean(errors.email)}
              aria-describedby={describedBy}
              value={fields.email}
              onChange={(e) => setField('email')(e.target.value)}
            />
          )}
        </Field>

        <Field label="Phone (optional)" {...errProp(errors.phone)}>
          {({ inputId, describedBy }) => (
            <TextInput
              id={inputId}
              type="tel"
              name="phone"
              autoComplete="tel"
              maxLength={PHONE_MAX}
              invalid={Boolean(errors.phone)}
              aria-describedby={describedBy}
              value={fields.phone}
              onChange={(e) => setField('phone')(e.target.value)}
            />
          )}
        </Field>

        <Field label="Company (optional)" {...errProp(errors.company)}>
          {({ inputId, describedBy }) => (
            <TextInput
              id={inputId}
              name="company"
              autoComplete="organization"
              maxLength={COMPANY_MAX}
              invalid={Boolean(errors.company)}
              aria-describedby={describedBy}
              value={fields.company}
              onChange={(e) => setField('company')(e.target.value)}
            />
          )}
        </Field>
      </div>

      <div className="mt-6">
        <Field label="Enquiry type" {...errProp(errors.enquiryType)}>
          {({ inputId }) => (
            <Select
              id={inputId}
              label="Enquiry type"
              options={ENQUIRY_TYPES}
              value={enquiryType}
              onChange={setEnquiryType}
              placeholder="Select an enquiry type…"
            />
          )}
        </Field>
      </div>

      {isCareers ? (
        <div role="note" className="mt-6 rounded border border-line bg-paper/60 p-4 text-ink">
          <p>
            Looking to join the team? Open roles are listed on our{' '}
            <Link to="/careers" className="underline">Careers page</Link>.
          </p>
        </div>
      ) : (
        <>
          <div className="mt-6">
            <Field
              label="Message"
              hint={`Between ${MESSAGE_MIN} and ${MESSAGE_MAX.toLocaleString('en-AU')} characters.`}
              {...errProp(errors.message)}
            >
              {({ inputId, describedBy }) => (
                <TextArea
                  id={inputId}
                  name="message"
                  rows={6}
                  maxLength={MESSAGE_MAX}
                  required
                  invalid={Boolean(errors.message)}
                  aria-describedby={describedBy}
                  value={fields.message}
                  onChange={(e) => setField('message')(e.target.value)}
                />
              )}
            </Field>
          </div>

          {/* Honeypot — visually and programmatically hidden; humans never fill it,
              the API rejects submissions that do. Named "website" so naive bots
              autofill it; the payload key is `website` too (the API's contract). */}
          <div className="hidden" aria-hidden="true">
            <label htmlFor="enquiry-website">Website</label>
            <input
              id="enquiry-website"
              type="text"
              name="website"
              tabIndex={-1}
              autoComplete="off"
              value={honeypot}
              onChange={(e) => setHoneypot(e.target.value)}
            />
          </div>

          <div ref={turnstileHostRef} className="mt-6" data-testid="turnstile-host" />

          <div className="mt-6">
            <Button type="submit" disabled={phase === 'submitting'}>
              {phase === 'submitting' ? 'Sending…' : 'Send enquiry'}
            </Button>
          </div>
        </>
      )}
    </form>
  );
}
